import React, { useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import styles from './index.module.scss';

import { UiKit } from 'components';
import * as EDITOR from './stubs';

/**
 * @param {Object} props
 * @param {string} props.label - Label for the editor
 * @param {string} props.value - HTML content value
 * @param {boolean} props.disabled - Whether the editor is disabled
 * @param {(value: string) => void} props.onChange - Callback when content changes
 * @param {string} props.error - Error message to display
 * @param {number} props.height - Height of the editor in pixels
 * @param {string} props.placeholder - Placeholder text
 * @param {Object} props.modules - Custom Quill modules (optional)
 * @param {Array} props.formats - Custom Quill formats (optional)
 * @param {string} props.theme - Quill theme ('snow' or 'bubble')
 * @param {...Object} restProps - Additional props passed to ReactQuill
 */
export default function HtmlEditor({
  label = '',
  value = '',
  onChange = () => {},
  disabled = false,
  error = '',
  height = 400,
  placeholder = 'Enter your content...',
  modules = null,
  formats = null,
  theme = null,
  ...restProps
}) {
  // Memoize the configuration to prevent unnecessary re-renders
  const editorConfig = useMemo(
    () => ({
      modules: modules || EDITOR.MODULES,
      formats: formats || EDITOR.FORMATS,
      theme: theme || EDITOR.THEME
    }),
    [modules, formats, theme]
  );

  // Custom style for editor height
  const editorStyle = useMemo(
    () => ({
      height: `${height}px`
    }),
    [height]
  );

  return (
    <div className={styles.container} data-error={!!error}>
      <UiKit.Label text={label} />
      <div className={styles.editorWrapper} style={editorStyle}>
        <ReactQuill
          value={value}
          onChange={onChange}
          readOnly={disabled}
          placeholder={placeholder}
          modules={editorConfig.modules}
          formats={editorConfig.formats}
          theme={editorConfig.theme}
          className={disabled ? styles.disabled : ''}
          {...restProps}
        />
      </div>
      <UiKit.Error msg={error} hide={disabled} />
    </div>
  );
}
